/**
 * The Iran Poker Tour identity, as the pieces a table is built from.
 *
 * Straight from the brand guide rather than approximated: the spade is the
 * brand's own path, the card icon is its construction (a card tilted -8° with
 * a spade where the letter I would be), and the chip is its recipe — six edge
 * inserts every 60°, an inner ring, a mark in the middle.
 *
 * Drawn here rather than shipped as image files for the same reason the
 * avatars are: these appear a hundred times on a table, at a dozen sizes, in
 * four colourways. As markup they cost nothing to load, scale exactly, and
 * take their colours from the same tokens as everything else — so a table
 * theme changes the chips without anybody exporting anything.
 */

/** The brand's spade, in its own viewBox space of 0 0 100 110. */
export const SPADE_PATH =
  'M50 4 C45.5 14 38 23.5 28.5 32.5 C18.5 42 11 50.5 11 62 C11 75 21 84.5 33 84.5 ' +
  'C39 84.5 44.5 81.5 47.5 76.5 C46.5 88 41.5 98 33.5 105 L66.5 105 ' +
  'C58.5 98 53.5 88 52.5 76.5 C55.5 81.5 61 84.5 67 84.5 C79 84.5 89 75 89 62 ' +
  'C89 50.5 81.5 42 71.5 32.5 C62 23.5 54.5 14 50 4 Z';

export interface IconColours {
  /** The card itself. */
  readonly card: string;
  /** The spade on it. */
  readonly spade: string;
}

/** Ivory card, emerald spade — the version for dark and emerald grounds. */
export const ON_DARK: IconColours = { card: '#F7F4EE', spade: '#0F6B57' };
/** Inverted, for light grounds. */
export const ON_LIGHT: IconColours = { card: '#0F6B57', spade: '#F7F4EE' };

/**
 * The card icon: the mark on its own.
 *
 * Below about 32 pixels the guide says use this rather than the full monogram,
 * which is most of the places a table needs it — chip faces, the card back,
 * a favicon.
 */
export function CardIcon({
  colours = ON_DARK,
  className,
  title,
}: {
  colours?: IconColours;
  className?: string;
  title?: string;
}) {
  return (
    <svg className={className} viewBox="0 0 180 190" role={title === undefined ? 'presentation' : 'img'}>
      {title !== undefined && <title>{title}</title>}
      <g transform="rotate(-8 90 95)">
        <rect x="52" y="30" width="76" height="130" rx="9" fill={colours.card} />
        <path d={SPADE_PATH} transform="translate(65,67.5) scale(0.5)" fill={colours.spade} />
        <path d={SPADE_PATH} transform="translate(58,37) scale(0.12)" fill={colours.spade} />
      </g>
    </svg>
  );
}

/**
 * The monogram: the card standing in for the I, then an italic PT.
 *
 * Used at size — the felt watermark, the lobby — never small. The guide is
 * firm that the wordmark is only ever Kanit, so the text is set in it here
 * with no fallback that would quietly change the mark's shape; if Kanit has
 * not loaded, a system italic is a worse wrong than a moment of nothing.
 */
export function Monogram({
  colours = ON_DARK,
  className,
  title,
}: {
  colours?: IconColours;
  className?: string;
  title?: string;
}) {
  return (
    <svg className={className} viewBox="0 0 318 190" role={title === undefined ? 'presentation' : 'img'}>
      {title !== undefined && <title>{title}</title>}
      <g transform="rotate(-8 86 90)">
        <rect x="48" y="25" width="76" height="130" rx="9" fill={colours.card} />
        <path d={SPADE_PATH} transform="translate(61,62.5) scale(0.5)" fill={colours.spade} />
        <path d={SPADE_PATH} transform="translate(54,32) scale(0.12)" fill={colours.spade} />
      </g>
      <text
        x="140"
        y="155"
        fontFamily="Kanit"
        fontStyle="italic"
        fontWeight="800"
        fontSize="140"
        fill={colours.card}
      >
        PT
      </text>
    </svg>
  );
}

/** The four colourways a chip comes in, from the guide. */
export type ChipKind = 'brand' | 'ivory' | 'midnight' | 'red';

interface ChipColours {
  readonly body: string;
  readonly insert: string;
  readonly ring: string;
  readonly ink: string;
}

const CHIPS: Record<ChipKind, ChipColours> = {
  brand: { body: '#0F6B57', insert: '#F7F4EE', ring: '#F7F4EE', ink: '#F7F4EE' },
  ivory: { body: '#F7F4EE', insert: '#0F6B57', ring: '#0F6B57', ink: '#0F6B57' },
  midnight: { body: '#080A0F', insert: '#C9A24D', ring: '#C9A24D', ink: '#C9A24D' },
  red: { body: '#B3262E', insert: '#F7F4EE', ring: '#F7F4EE', ink: '#F7F4EE' },
};

/**
 * A chip.
 *
 * Six inserts every sixty degrees, an inner ring, and either a value or the
 * brand mark in the middle — which is the guide's recipe exactly. Without a
 * `value` it is the house chip and carries the card icon instead, the way the
 * emerald one in the set does.
 */
export function Chip({
  kind = 'brand',
  value,
  className,
}: {
  kind?: ChipKind;
  value?: number | string;
  className?: string;
}) {
  const c = CHIPS[kind];
  const inserts = [0, 60, 120, 180, 240, 300];

  return (
    <svg className={className} viewBox="0 0 100 100" role="presentation">
      <circle cx="50" cy="50" r="49" fill={c.body} />
      {inserts.map((angle) => (
        <rect
          key={angle}
          x="45.5"
          y="1.5"
          width="9"
          height="13"
          rx="2.5"
          fill={c.insert}
          transform={`rotate(${angle} 50 50)`}
        />
      ))}
      <circle cx="50" cy="50" r="33" fill="none" stroke={c.ring} strokeWidth="2.4" />
      {value === undefined ? (
        <g transform="translate(31,26) scale(0.21)">
          <rect x="52" y="30" width="76" height="130" rx="9" fill={c.ink} />
          <path d={SPADE_PATH} transform="translate(65,67.5) scale(0.5)" fill={c.body} />
        </g>
      ) : (
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="Kanit"
          fontWeight="800"
          fontSize="30"
          fill={c.ink}
        >
          {value}
        </text>
      )}
    </svg>
  );
}
